<script setup lang="ts">
import { ref } from 'vue';

import { AppImg, AppButton, AppRawButton } from '@libs/ui';

import {
  useCoreStore,
  useStatsigEventsStore,
} from '../../../../../../core/stores';
import type {
  AccommodationProductCardData,
  AccommodationSegmentContext,
} from '../../../../models';
import AccommodationProductCardContent from '../AccommodationProductCardContent.vue';
import AccommodationCardSelectedFooter from '../AccommodationCardSelectedFooter.vue';
import DateCardTag from '../../DateCardTag.vue';
import TripOverviewTrashIconButton from '../../../TripOverviewTrashIconButton.vue';

import MobileProductCardLayout from './MobileProductCardLayout.vue';
import MobileProductCardLayoutExperiment from './MobileProductCardLayoutExperiment.vue';

interface AccommodationProductCardMobileProps {
  productCardData: AccommodationProductCardData;
  context: AccommodationSegmentContext;
  shouldHideProductLink: boolean;
}

const props = defineProps<AccommodationProductCardMobileProps>();

interface AccommodationProductCardMobileEmits {
  (event: 'show-details'): void;
  (event: 'show-rooms'): void;
  (event: 'open-hotel-selection'): void;
  (event: 'remove-accommodation'): void;
}

const emit = defineEmits<AccommodationProductCardMobileEmits>();

const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();

const enableHoverColor = ref<boolean>(false);

function openHotelSelection(): void {
  statsigEventsStore.logExperimentAccommodationActionClicked(
    'changeButtonExperiment'
  );
  emit('open-hotel-selection');
}

function removeAccommodation(): void {
  statsigEventsStore.logExperimentIngredientRemoved('accommodation');
  emit('remove-accommodation');
}

function showDetailsExperiment(): void {
  if (!props.shouldHideProductLink) {
    showDetails('mainCard');
  }
}

function showDetails(
  clickSource: 'mainCard' | 'detailsButtonExperiment'
): void {
  emit('show-details');
  statsigEventsStore.logExperimentAccommodationActionClicked(clickSource);
}
</script>

<template>
  <MobileProductCardLayoutExperiment
    v-if="props.shouldHideProductLink"
    class="accommodation-product-card-mobile experiment"
    @click.stop="showDetailsExperiment()"
  >
    <template #top-section>
      <AppImg
        class="image"
        :src="productCardData.imgSrcs[0] || ''"
        :alt="$t('a11y.hotel_image')"
        @img-load-error="
          (src) =>
            coreStore.handleImageLoadError({
              src: src,
              source: 'accommodation-product-card-mobile-image',
            })
        "
      />
    </template>
    <template #content>
      <AccommodationProductCardContent
        :product-card-content="props.productCardData.productCardContent"
        :context="props.context"
        :print-mode="false"
        :should-hide-product-link="props.shouldHideProductLink"
        @mouse-enter-content="enableHoverColor = true"
        @mouse-leave-content="enableHoverColor = false"
        @show-details="showDetailsExperiment"
        @show-rooms="emit('show-rooms')"
      />
    </template>
    <template #footer>
      <div class="button-wrapper">
        <AppRawButton
          class="details"
          @click="showDetails('detailsButtonExperiment')"
        >
          {{ $t('planner.details') }}
        </AppRawButton>
        <AppButton
          state="enabled"
          type="primary"
          size="small"
          :ariaLabel="$t('a11y.change_accommodation')"
          @click="openHotelSelection"
        >
          {{ $t('buttons.stay') }}
        </AppButton>
      </div>
    </template>
    <template #top-section-tag>
      <DateCardTag :date="props.productCardData.date" />
    </template>
    <template #trash-icon>
      <TripOverviewTrashIconButton
        class="trash-icon"
        :with-border="true"
        :rounded="false"
        :ariaLabel="$t('a11y.remove_stay')"
        @remove="removeAccommodation"
      />
    </template>
  </MobileProductCardLayoutExperiment>
  <MobileProductCardLayout
    v-else
    class="accommodation-product-card-mobile"
    @click.stop="showDetails"
  >
    <template #top-section>
      <AppImg
        class="image"
        :src="productCardData.imgSrcs[0] || ''"
        :alt="$t('a11y.hotel_image')"
        @img-load-error="
          (src) =>
            coreStore.handleImageLoadError({
              src: src,
              source: 'accommodation-product-card-mobile-image',
            })
        "
      />
    </template>
    <template #content>
      <AccommodationProductCardContent
        :product-card-content="props.productCardData.productCardContent"
        :context="props.context"
        :print-mode="false"
        :should-hide-product-link="props.shouldHideProductLink"
        @mouse-enter-content="enableHoverColor = true"
        @mouse-leave-content="enableHoverColor = false"
        @show-details="showDetails('mainCard')"
        @show-rooms="emit('show-rooms')"
      />
    </template>
    <template #footer>
      <AccommodationCardSelectedFooter
        :ingredient-index="props.context.ingredientIndex"
      />
    </template>
    <template #top-section-tag>
      <DateCardTag :date="props.productCardData.date" />
    </template>
  </MobileProductCardLayout>
</template>

<style lang="scss" scoped>
.accommodation-product-card-mobile {
  &.experiment {
    position: relative;

    .trash-icon {
      position: absolute;
      top: 0;
      right: 0;
    }
    .image {
      aspect-ratio: 4 / 3;
      max-height: 200px;
    }

    .button-wrapper {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      gap: var(--spacing-16);

      .details {
        @include font-label-m-light;
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
  .image {
    aspect-ratio: 4 / 3;
  }
}
</style>
